<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <FormulateForm
            v-if="formReady"
            :values="initialFormData"
            :name="formName"
            #default="{ isValid, isLoading }"
            @submit="handleSubmit"
        >
            <FormulateInput
                name="version_notes"
                type="textarea"
                label="Notes about this version?"
                placeholder="Add any notes about this specific version (optional)"
                rows="3"
                help="Optional"
                :disabled="isLocked"
                :outer-class="['formulate-horizontal']"
            />
            <FormulateInput
                id="dochubfile-formulate-file-input"
                ref="formulateFileInput"
                type="file"
                name="files"
                label="Files"
                validation="required"
                validation-name="File(s)"
                :disabled="isLocked"
                :file-remove-class="isLocked ? 'd-none' : []"
                :upload-area-mask-class="['bg-gray-100']"
                :outer-class="['formulate-horizontal']"
                :uploader="formulateUploader"
                upload-behavior="live"
                multiple     
            />
            <b-alert show variant="info" class="mt-2 mb-0 small text-muted">
                NOTE: If uploading videos, please upload the file (one file per item), then contact our support team to have them converted to streaming video.
            </b-alert>
            <!-- add read-only version_number?  -->
            <div class="mt-4 mb-3 d-flex justify-content-center">
                <b-button
                    variant="gray-secondary"
                    size="lg"
                    class="mr-3"
                    @click="triggerCancel"
                >
                    <span>Cancel</span>
                </b-button>
                <b-button
                    variant="primary"
                    size="lg"
                    :disabled="!isValid || isLoading || saving || uploadPending || isLocked"
                    @click="triggerSubmit"
                >
                    <b-spinner small v-if="saving" key="uploading" class="mr-2"></b-spinner>
                    <b-icon v-else key="uploadReady" icon="upload" class="mr-2"></b-icon>
                    <span>{{ saving ? 'Saving' : 'Save' }}</span>
                </b-button>
            </div>
        </FormulateForm>
    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import { asyncSplFileSlinger } from '../../js/file-helpers';
import cloneDeep from 'lodash/cloneDeep';

export default {
    name: 'DocHubResourceVersionForm',
    components: {},
    mixins: [
        errorHandling,
    ],
    props: {
        resourceId: {
            type: String,
            required: true,
        },
        versionData: {  // if no version ID, then we are doing a CREATE, otherwise provide one to do an update
            type: Object,
            required: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,  
            saving: false,
            formName: 'versionForm',
            formReady: false,  // set true when you're done prepping initial data, if any
            uploadPending: false,  // are uploads happening?
            initialFormData: {},  // if you are updating existing, must put here (goes to value prop, not v-model)
            //formData: {},  // just set initialFormData - then get the 'current' data from the @submit handler
            fileCategory: 'dochubfile',  // the s3file category in our backend 
        }
    },
    computed: {
        isLocked() {
            // do we want to lock form?  add logic later...
            return false;
        },
        isUpdate() {
            return !!this.versionData?.id;
        },
    },
    mounted() {
        // do something depending on being a create/update?
        if (this.isUpdate) {
            this.setInitialFormData(this.versionData);
            this.formReady = true;
        } else {
            this.formReady = true;
        }
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        setInitialFormData(versionData) {
            // pass in versionData from your API, this has to format the 'files' array as Formulate expects
            // multiple file inputs should be an array of objects with at least:
            //   'id' (for us), 'url' key (for formulate) and 'name' is optional (should be filename)
            // our INCOMING data (from output serializer has 'files_list' - nested array of files, and 'files' - just the IDs, used for updates)
            // we need to get the object data from 'files_list' and set it on 'files' here for use in the form
            // THEN, before sending to API, make a flat list of JUST THE IDS of new/updated IDs
            let { files_list, ...otherData } = versionData;
            this.initialFormData = cloneDeep(otherData);  // copy this first, then we'll add files back in after 
            if (files_list && Array.isArray(files_list) && files_list.length) {
                let initialFiles = [];
                files_list.forEach(f => {
                    if (!f.id || !f.download_url) {  // could also just use 'public_url' - but this person should have perms to get download_url...
                        return;  // GOTTA HAVE 'id' (for our own backend) and 'url' for formulate
                    }
                    const fileObj = {id: f.id, url: f.download_url};
                    if (f.filename) {
                        fileObj['name'] = f.filename;
                    }
                    initialFiles.push(fileObj);
                })
                this.initialFormData['files'] = initialFiles;  // as long as 'files' is still the key you want it as when posting to API
            }
        },
        triggerSubmit() {
            this.$formulate.submit(this.formName);
        },
        triggerCancel() {
            this.$emit('cancel');  // also need to reset things?  or let it get destroyed?
        },
        formatDataForSave(formData) {
            // do anything with the data before sending to API
            // must get the file IDs out and send those in array of 'files'
            console.log('data we need to format for save:', formData);
            // once again, we need to transform the structure of 'files' -- from formulate, we'll have array of objects, hopefully with our ID in there
            // we need to pull out the ID and just send back a flat list of IDs to our backend
            let { files, ...otherData } = formData;
            const dataForApi = cloneDeep(otherData);  // copy this first, then we'll add files back in after 
            if (files && Array.isArray(files) && files.length) {
                let fileIdsForApi = files.map(f => f.id);
                dataForApi['files'] = fileIdsForApi;  // as long as 'files' is still the key you want it as when posting to API
            }
            // make sure we have resource id in there also
            dataForApi['resource'] = this.resourceId;
            return dataForApi;
        },
        setSavingStatus(isSaving) {
            this.saving = isSaving;
            this.$emit('loading-status', isSaving);
        },
        async handleSubmit(formData) {  // called on the FormulateForm @submit event
            // this should be async, return a Promise, so Formulate shows isLoading while loading
            console.log('*** From handleSubmit, here is formData we got:');
            console.log(formData);
            let versionDataToSubmit = this.formatDataForSave(formData);
            this.setSavingStatus(true);
            let returnData;
            try {
                if (this.isUpdate) {
                    // NOTE - we don't allow updates to versions right now... but maybe later...
                    const versionId = this.versionData.id;
                    const resp = await suppliosApi.dochubResourceVersion.update(versionId, versionDataToSubmit);  
                    returnData = resp.data;
                    console.log('got response from updating version', returnData);
                } else {  // if NOT an update, create new version
                    const resp = await suppliosApi.dochubResource.create_new_version(this.resourceId, versionDataToSubmit);
                    returnData = resp.data;
                    console.log('got response from creating new version', returnData);
                }
            } catch (err) {
                this.apiErrorHandler(err);
            }
            if (returnData) {
                if (this.isUpdate) {
                    this.$emit('updated', returnData);
                } else {
                    this.$emit('created', returnData);
                }
                this.$emit('saved', returnData);  // common for both update/create
            }
            this.setSavingStatus(false);
            return returnData;  // make sure not to return until after you set saving status back to false
        },
        async formulateUploader(file, progress, error, options) {
            // pass this function to 'uploader' prop of formulate-input 
            // must return a Promise that resolves to array of objects, at least with 'url' key in each object (but you also expect 'id' for your own backend handling)
            // and must accept 4 args (file, progress, error, option)
            this.uploadPending = true;  // set our flag to true so we can block things while uploading
            let file_category = this.fileCategory;
            let parentId = this.versionId;  // the ID of the 'parent' for this file - optional
            let title = '';  // you could pass title to asyncSplFileSlinger if you know it in advance
            let uploadResponse = await asyncSplFileSlinger(file_category, file, progress, error, options, title, parentId);
            this.uploadPending = false;
            console.log('Dumping resp from our file uploader:');
            console.log(uploadResponse);
            return uploadResponse;  // must return a promise here so formulate knows upload is done 
        },
    }
}
</script>

<style>

</style>